<template>
  <a-modal
    :visible="visible"
    :destroy-on-close="true"
    @cancel="closeModal"
    centered
    width="540px"
    wrapClassName="create-business-modal"
  >
    <h2 style="margin-bottom: 0">Estás a un paso de tener tu propio</h2>
    <h2>Contact Center en la nube</h2>
    <p style="text-align: center">
      Estos son los detalles de tu plan según lo coordinado con el equipo de ventas Beex.
    </p>
    <a-card class="modal-card">
      <img :src="urlImage(planTypeIcon(plan?.plan))" alt="plan" style="height: 56px" />
      <h2>{{ PlanTypeText[plan.plan as PlanType] }}</h2>
      <div class="price" v-if="payment">
        <span class="price_slot">
          ${{ payment?.price_per_slot }} 
        </span>
        <span class="price_description">
          por slot/mes
        </span>
        <p class="price_info">
          <InfoCircleOutlined />
          No incluye precio de alta o IGV.
        </p>
        <p class="price_type">
          <a-tag :color="PlanPayTypeColor[plan.type_plan_license]">
            {{ PlanPayTypeText[plan.type_plan_license] }}
          </a-tag>
          <a-tag>Personalizado</a-tag>
        </p>
      </div>
      <a-divider style="margin: 16px 0" />
      <div class="section_price__details">
        <div class="section_price__details--item" v-if="plan.type_plan_license == PlanPayType.Prepaid">
          <span>
            <a-tooltip placement="left">
              <template #title>
                Monto calculado del número de slots multiplicado por el precio por slot (total o prorrateo según la fecha del registro).
              </template>
              <InfoCircleOutlined />
            </a-tooltip>
            Precio por slots ({{ payment?.slots_per_month }} slots 
            x ${{ !payment?.is_prorated ? payment?.price_per_slot : payment?.prorated_price_per_slot }} c/u)
          </span>
          <span>${{ payment?.total_base_price }}</span>
        </div>
        <div v-if="configuration?.aditional_charge && plan.type_plan_license == PlanPayType.Prepaid" class="section_price__details--item">
          <span>
            <a-tooltip 
              placement="left" 
              :title="`Cálculo del ${ payment?.tax * 100 }% de IGV sobre el precio por slot multiplicado por el número de slots. Es un costo adicional separado.`"
            >
              <InfoCircleOutlined />
            </a-tooltip>
            IGV ({{ payment?.tax * 100 }}%) de slots
          </span>
          <span>${{ payment?.total_tax_price }}</span>
        </div>
        <div class="section_price__details--item">
          <span>Precio de alta (único pago)</span>
          <span>${{ payment?.high_price || 0 }}</span>
        </div>
        <div v-if="configuration?.aditional_charge" class="section_price__details--item">
          <span>
            <a-tooltip 
              placement="left" 
              :title="`Cálculo del ${ payment?.tax * 100 }% de IGV sobre el precio de alta. Es un costo adicional separado.`"
            >
              <InfoCircleOutlined />
            </a-tooltip>
            IGV ({{ payment?.tax * 100 }}%) de precio de alta
          </span>
          <span>${{ payment?.total_tax_high_price }}</span>
        </div>
      </div>
      <a-divider class="divider_item" />
      <div class="section_price__details">
        <div class="section_price__details--total">
          <h2 style="font-size: 1.3em">
            <a-tooltip placement="left">
              <template #title>
                <span v-if="plan.type_plan_license == PlanPayType.Prepaid">
                  Resultado de la suma de todos los conceptos de pago listados arriba según la configuración de su plan.
                </span>
                <span v-else>
                  Incluye el precio de alta, más su IGV ({{ payment?.tax * 100 }}%), si se muestra listado. 
                  En la próxima factura, se coordinará el próximo pago.
                </span>
              </template>
              <InfoCircleOutlined />
            </a-tooltip>
            Total a pagar:
          </h2>
          <h2>
            <!-- ${{ (configuration.slots_per_month?.value * configuration.price_per_slot?.value) + Number(configuration.high_price?.value || 0)}} -->
            ${{ payment?.total_price }}
          </h2>
        </div>
        <div class="section_price__details--info">
          <span v-if="plan.type_plan_license == PlanPayType.Prepaid">
            Pago único, luego 
            ${{ configuration?.aditional_charge ? (payment?.slots_per_month * payment?.price_per_slot * 1.18) : (payment?.slots_per_month * payment?.price_per_slot) }} 
            mensual. No incluye consumos.
          </span>
          <span v-else>
            Pago único al registrarse. El equipo de ventas coordinará el pago mensual en su próxima facturación.
          </span>
        </div>
      </div>
    </a-card>
    <template #footer>
      <div class="footer">
        <a-button @click="closeModal">
          Cancelar
        </a-button>
        <a-button 
          class="button payment-button"
          type="primary"
          v-if="amount == 0 && plan.type_plan_license == PlanPayType.Postpaid"
        >
          Realizar el pago
        </a-button>
        <PayUPaymentButton
          v-else
          title=""
          name="Realizar el pago"
          :customerPlanConfigToken="route.query.custom_token"
          :amount="amount"
          :type="ProductType.Register"
          @success="emit('registerCustomer')"
          @reject="handleOpenModal('error')"
        />
      </div>
    </template>
  </a-modal>
  <TransactionStateModal v-model:open="transactionModal.open" :state="transactionModal.state" />
</template>

<script setup lang="ts">
import { computed, reactive } from "vue";
import { InfoCircleOutlined } from "@ant-design/icons-vue";
import PayUPaymentButton from "@/app/shared/components/buttons/PayUPaymentButton.vue";
import { useRoute } from "vue-router";
import TransactionStateModal from "@/app/settings/components/modals/TransactionStateModal.vue";
import { ProductType } from "@/@types/settings/billing";
import {
  PlanPayType,
  PlanPayTypeText,
  PlanPayTypeColor,
  PlanType,
  PlanTypeIcon,
  PlanTypeText,
} from "@/app/common/types";

const route = useRoute();

const props = defineProps({
  plan: {
    type: Object,
    required: true,
  },
  visible: {
    type: Boolean,
    required: true,
  }
});

const emit = defineEmits(["update:visible", "registerCustomer"]);

const amount = computed(() => {
  if (props.plan.type_plan_license == PlanPayType.Prepaid) 
    return (payment.value?.total_price || 0)
  else if (props.plan.type_plan_license == PlanPayType.Postpaid)
    return Number(payment.value?.total_price || 0)
});
const transactionModal = reactive({
  open: false,
  state: "success",
});
const configuration = computed(() => props.plan?.configuration );
const payment = computed(() => props.plan?.payment_detail );
// const TAXES = 18;

const handleOpenModal = (state: string) => {
  transactionModal.open = true;
  transactionModal.state = state;
};

// const planPrice = computed(() => {
//   return configuration.value.slots_per_month.value * configuration.value.price_per_slot.value;
// });

// const taxesPrice = computed(() => {
//   return planPrice.value * TAXES / 100;
// });

const planTypeIcon = (id: number) => {
  return PlanTypeIcon[id as keyof typeof PlanTypeIcon];
};

const urlImage = (image: string) => {
  return new URL(`../../../common/assets/${image}`, import.meta.url).href;
};

const closeModal = () => {
  emit("update:visible", false);
}
</script>

<style lang="sass" scoped>
.state__information
  text-align: center
  margin-top: 40px

.state
  font-size: 24px
  font-weight: 600
  color: $gray-11
  margin-bottom: 16px

.state__image
  text-align: center

.text--loading
  color: $gray_dark_300
  font-style: oblique
  margin: 0

.character
  animation: spin 1s ease infinite

@keyframes spin
  0%
    transform: rotate(360deg)

  100%
    transform: rotate(0deg)

.modal-card
  text-align: left
  h2
    margin-bottom: 3px
.price
  &_slot
    font-size: 22px
    font-weight: 600
  &_description
    font-size: 14px
    color: $gray-8
  &_info
    color: $gray-8
    font-style: italic
  &_type
    margin-top: 10px

.section_price
  &__details
    text-align: right
    button
      padding-right: 0
    &--item, &--total
      display: flex
      align-items: center
      justify-content: end
      text-align: right
      gap: 20px
      margin-bottom: 8px
    &--total
      margin-bottom: 0
    &--info
      display: flex
      align-content: center
      justify-content: end
      color: $gray-7
      font-style: italic
    &_type
      margin-top: 10px

.divider_item
  margin: 10px 0

.footer
  padding: 0 20px
  display: flex
  justify-content: space-between

.button
  width: 100%
  margin-bottom: 10px
</style>

<style>
.create-business-modal .ant-modal-content {
  border-radius: 20px;
}

.create-business-modal .ant-modal-body {
  text-align: center;
  padding: 0 20px;
}
</style>
